import { useQuery } from '@tanstack/react-query';
import { Utensils, ShoppingBag, Bike, Minus, Plus, ArrowLeft, ArrowRight } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { UserSelect } from '@/components/common/entity-select';
import { ordersApi } from '@/lib/api/services/orders.api';
import { cn } from '@/lib/utils';
import { CartLines } from './CartLines';
import type { useOrderState } from './useOrderState';
import type { ServiceType } from '@/lib/api/services/orders.api';

type OrderState = ReturnType<typeof useOrderState>;

interface Props {
  state: OrderState;
  locationId: number;
  onBack: () => void;
  onContinue: () => void;
}

type ServiceTypeOption = { value: ServiceType; label: string; icon: React.ComponentType<{ className?: string }>; sub: string };

export function Step2CustomerInfo({ state, locationId, onBack, onContinue }: Props) {
  const { t } = useLingui();
  const { customer, patchCustomer, toggleAllergen } = state;

  const SERVICE_TYPES: ServiceTypeOption[] = [
    { value: 'DINE_IN',  label: t`Dine-in`,  icon: Utensils,    sub: t`Table service`    },
    { value: 'TAKEOUT',  label: t`Takeout`,  icon: ShoppingBag, sub: t`Counter pickup`   },
    { value: 'DELIVERY', label: t`Delivery`, icon: Bike,        sub: t`Courier dispatch` },
  ];
  const ALLERGEN_OPTIONS = [t`Gluten`, t`Dairy`, t`Shellfish`, t`Nuts`, t`Eggs`, t`Soy`, t`Fish`];
  const RESERVATION_TAGS = [t`None`, t`Birthday`, t`Anniversary`, t`VIP / Comp`, t`Allergy alert`];

  // Fetch tables for this location
  const { data: tables = [] } = useQuery({
    queryKey: ['order-dining-tables', locationId],
    queryFn: () => ordersApi.getDiningTables({ locationId }),
  });

  // Group tables by section
  const sections = [...new Set(tables.map((t) => t.section))];

  const filteredTables = customer.section
    ? tables.filter((t) => t.section === customer.section)
    : tables;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_25rem] gap-6 items-start">
      {/* ── Left: forms ── */}
      <div className="space-y-5">
        {/* Service type */}
        <div className="rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5">
          <div className="text-[1.125rem] font-bold mb-1"><Trans>Service type</Trans></div>
          <p className="text-[0.8125rem] text-(--color-text-secondary) mb-4"><Trans>Where is this ticket going?</Trans></p>
          <div className="grid grid-cols-3 gap-3">
            {SERVICE_TYPES.map(({ value, label, icon: Icon, sub }) => (
              <button
                key={value}
                type="button"
                onClick={() => patchCustomer({ serviceType: value })}
                className={cn(
                  'flex flex-col items-start gap-1.5 p-4 border rounded-[0.85rem] bg-(--color-surface-content) text-left transition-all',
                  customer.serviceType === value
                    ? 'border-(--color-text-primary) ring-1 ring-foreground bg-(--color-surface-muted)/30'
                    : 'border-(--color-border) hover:border-muted-foreground',
                )}
              >
                <Icon className="w-5 h-5 text-(--color-text-secondary)" />
                <div className="text-[0.9375rem] font-bold text-(--color-text-primary)">{label}</div>
                <div className="text-[0.78125rem] text-(--color-text-secondary)">{sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Table & party — only for dine-in */}
        <div
          className={cn(
            'rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5 transition-opacity',
            customer.serviceType !== 'DINE_IN' && 'opacity-50 pointer-events-none',
          )}
        >
          <div className="text-[1.125rem] font-bold mb-1"><Trans>Table &amp; party</Trans></div>
          <p className="text-[0.8125rem] text-(--color-text-secondary) mb-4"><Trans>Pick a table and enter the party size.</Trans></p>
          <div className="grid grid-cols-2 gap-4">
            {/* Section */}
            <div className="space-y-1.5">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Section</Trans>
              </Label>
              <Select
                value={customer.section}
                onValueChange={(v) => patchCustomer({ section: v, tableId: null, tableName: '' })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={t`All sections`} />
                </SelectTrigger>
                <SelectContent>
                  {sections.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="space-y-1.5">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Table</Trans>
              </Label>
              <Select
                value={customer.tableId?.toString() ?? ''}
                onValueChange={(v) => {
                  const tbl = tables.find((t) => t.id === Number(v));
                  patchCustomer({ tableId: Number(v), tableName: tbl?.name ?? '' });
                }}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder={t`Select table`} />
                </SelectTrigger>
                <SelectContent>
                  {filteredTables.map((tbl) => (
                    <SelectItem key={tbl.id} value={String(tbl.id)}>
                      {tbl.name} — {tbl.section} ({tbl.seatCount} <Trans>top</Trans>)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Guests */}
            <div className="space-y-1.5">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Guests</Trans>
              </Label>
              <div className="flex items-stretch h-11 border border-(--color-border) rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => patchCustomer({ guestCount: Math.max(1, customer.guestCount - 1) })}
                  className="w-11 bg-(--color-surface-muted) hover:bg-(--color-surface-muted)/80 flex items-center justify-center"
                >
                  <Minus className="w-4 h-4 text-(--color-text-primary)" />
                </button>
                <input
                  type="number"
                  value={customer.guestCount}
                  min={1}
                  max={20}
                  onChange={(e) => patchCustomer({ guestCount: Math.max(1, Number(e.target.value)) })}
                  className="flex-1 text-center border-0 focus:ring-0 font-semibold bg-(--color-surface-content) text-(--color-text-primary) text-[0.875rem] outline-none"
                />
                <button
                  type="button"
                  onClick={() => patchCustomer({ guestCount: Math.min(20, customer.guestCount + 1) })}
                  className="w-11 bg-(--color-surface-muted) hover:bg-(--color-surface-muted)/80 flex items-center justify-center"
                >
                  <Plus className="w-4 h-4 text-(--color-text-primary)" />
                </button>
              </div>
            </div>

            {/* Server */}
            <div className="space-y-1.5">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Server</Trans>
              </Label>
              <UserSelect
                value={customer.serverId != null ? String(customer.serverId) : null}
                onChange={(id, srv) =>
                  patchCustomer({
                    serverId: Number(id),
                    serverName: `${srv.firstName ?? ''} ${srv.lastName ?? ''}`.trim(),
                  })
                }
                onClear={() => patchCustomer({ serverId: null, serverName: '' })}
                placeholder={t`Unassigned`}
                triggerClassName="h-11"
              />
            </div>
          </div>
        </div>

        {/* Cover details */}
        <div className="rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5">
          <div className="text-[1.125rem] font-bold mb-1"><Trans>Cover details</Trans></div>
          <p className="text-[0.8125rem] text-(--color-text-secondary) mb-4">
            <Trans>Optional — useful for VIPs and call-ahead orders.</Trans>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Guest name</Trans>
              </Label>
              <Input
                value={customer.guestName}
                onChange={(e) => patchCustomer({ guestName: e.target.value })}
                placeholder={t`e.g. R. Okonkwo`}
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Phone</Trans>
              </Label>
              <Input
                value={customer.phone}
                onChange={(e) =>
                  patchCustomer({ phone: e.target.value.replace(/\D/g, "") })
                }
                placeholder="5550104127"
                inputMode="numeric"
                pattern="[0-9]*"
                className="h-11"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide">
                <Trans>Reservation tag</Trans>
              </Label>
              <div className="flex items-center gap-2 flex-wrap">
                {RESERVATION_TAGS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() =>
                      patchCustomer({ reservationTag: customer.reservationTag === tag ? '' : tag })
                    }
                    className={cn(
                      'px-3.5 py-2 rounded-full border text-[0.78125rem] font-semibold transition-colors',
                      customer.reservationTag === tag
                        ? 'bg-(--color-text-primary) text-(--color-surface-page) border-(--color-text-primary)'
                        : 'bg-(--color-surface-muted) border-(--color-border) text-(--color-text-primary) hover:bg-(--color-surface-muted)/70',
                    )}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Dietary & notes */}
        <div className="rounded-2xl border border-(--color-border) bg-(--color-surface-content) p-5">
          <div className="text-[1.125rem] font-bold mb-1"><Trans>Dietary &amp; notes</Trans></div>
          <p className="text-[0.8125rem] text-(--color-text-secondary) mb-4">
            <Trans>Allergens flag automatically on the line.</Trans>
          </p>

          <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide mb-2 block">
            <Trans>Allergens</Trans>
          </Label>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {ALLERGEN_OPTIONS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggleAllergen(a)}
                className={cn(
                  'px-3.5 py-2 rounded-full border text-[0.78125rem] font-semibold transition-colors',
                  customer.allergens.includes(a)
                    ? 'bg-(--color-text-primary) text-(--color-surface-page) border-(--color-text-primary)'
                    : 'bg-(--color-surface-muted) border-(--color-border) text-(--color-text-primary) hover:bg-(--color-surface-muted)/70',
                )}
              >
                {a}
              </button>
            ))}
          </div>

          <Label className="text-[0.75rem] font-semibold text-(--color-text-secondary) uppercase tracking-wide mb-2 block">
            <Trans>Kitchen notes</Trans>
          </Label>
          <Textarea
            value={customer.kitchenNotes}
            onChange={(e) => patchCustomer({ kitchenNotes: e.target.value })}
            placeholder={t`e.g. Pasta al dente, sauce on the side, no garnish.`}
            className="min-h-[6rem]"
          />
        </div>
      </div>

      {/* ── Right: Ticket preview panel ── */}
      <aside className="lg:sticky lg:top-3">
        <div className="bg-(--color-surface-content) border border-(--color-border) rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-(--color-border) flex items-center justify-between">
            <div>
              <div className="text-[0.6875rem] tracking-wider text-(--color-text-secondary) font-semibold uppercase">
                <Trans>Ticket Preview</Trans>
              </div>
              <div className="text-[1.125rem] font-bold">
                {customer.tableName || <Trans>No table</Trans>}
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-(--color-order-pickup-bg) text-(--color-order-pickup-fg) text-[0.6875rem] font-bold">
              <Trans>DETAILS</Trans>
            </span>
          </div>

          {/* Summary grid */}
          <div className="p-5 grid grid-cols-2 gap-y-4 gap-x-3 text-[0.8125rem] border-b border-(--color-border)">
            {[
              { label: t`Service`, value: SERVICE_TYPES.find((s) => s.value === customer.serviceType)?.label ?? customer.serviceType },
              { label: t`Guests`,  value: customer.guestCount },
              { label: t`Server`,  value: customer.serverName || '—' },
              { label: t`Guest`,   value: customer.guestName || '—' },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="text-(--color-text-secondary) text-[0.6875rem] uppercase tracking-wider">{label}</div>
                <div className="font-semibold text-[0.875rem] mt-1">{value}</div>
              </div>
            ))}
          </div>

          {/* Cart mini */}
          <div className="p-3 max-h-[16.25rem] overflow-y-auto">
            <CartLines lines={state.cart} onSetQty={state.setQty} compact />
          </div>

          {/* Totals */}
          <div className="px-5 py-4 border-t border-(--color-border) text-[0.8125rem] space-y-1.5">
            <div className="flex justify-between">
              <span className="text-(--color-text-secondary)"><Trans>Items</Trans></span>
              <span className="font-semibold">{state.itemCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-(--color-text-secondary)"><Trans>Subtotal</Trans></span>
              <span className="font-semibold">${state.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[1rem] pt-2 mt-1 border-t border-(--color-border)">
              <span className="font-bold"><Trans>Total</Trans></span>
              <span className="font-extrabold">${state.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Nav */}
          <div className="p-3 border-t border-(--color-border) flex items-center gap-2 bg-(--color-surface-muted)">
            <Button variant="outline" onClick={onBack} className="flex-1 h-11 gap-2">
              <ArrowLeft className="w-4 h-4" /><Trans>Back</Trans>
            </Button>
            <Button onClick={onContinue} className="flex-1 h-11 gap-2">
              <Trans>Continue</Trans><ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
